import type { PepLocale } from "../i18n/types";

/** Why ITaaS — conviction page content (FR/EN). Portfolio-safe: no GA / HIS / SSO / Mobile Money claims. */

export const WHY_PLATFORM_NODES_FR = [
  "Edu",
  "RH",
  "Inventory",
  "ERP",
  "MinePortal",
  "Security",
] as const;

export const WHY_PLATFORM_NODES_EN = [
  "Edu",
  "HR",
  "Inventory",
  "ERP",
  "MinePortal",
  "Security",
] as const;

/** Ecosystem nodes for Why Hero — 8 modules evenly on the orbit (HD mockup order). */
export const WHY_ECOSYSTEM_MODULES = [
  {
    id: "edu",
    labelFr: "ITaaS EDU",
    labelEn: "ITaaS EDU",
    subtitleFr: "Éducation",
    subtitleEn: "Education",
    tone: "edu",
    icon: "education" as const,
  },
  {
    id: "hospitol",
    labelFr: "ITaaS Hospital",
    labelEn: "ITaaS Hospital",
    subtitleFr: "Santé",
    subtitleEn: "Healthcare",
    tone: "hospitol",
    icon: "health" as const,
  },
  {
    id: "rh",
    labelFr: "ITaaS RH",
    labelEn: "ITaaS RH",
    subtitleFr: "Ressources Humaines",
    subtitleEn: "Human Resources",
    tone: "rh",
    icon: "hr" as const,
  },
  {
    id: "inventory",
    labelFr: "ITaaS Inventory",
    labelEn: "ITaaS Inventory",
    subtitleFr: "Stocks & Supply Chain",
    subtitleEn: "Stock & Supply Chain",
    tone: "inventory",
    icon: "ops" as const,
  },
  {
    id: "security",
    labelFr: "ITaaS Security",
    labelEn: "ITaaS Security",
    subtitleFr: "Sécurité",
    subtitleEn: "Security",
    tone: "security",
    icon: "security" as const,
  },
  {
    id: "autoid",
    labelFr: "ITaaS AutoID",
    labelEn: "ITaaS AutoID",
    subtitleFr: "Traçabilité & Identification",
    subtitleEn: "Traceability & Identification",
    tone: "autoid",
    icon: "ops" as const,
  },
  {
    id: "erp",
    labelFr: "ITaaS ERP",
    labelEn: "ITaaS ERP",
    subtitleFr: "Gestion d'entreprise",
    subtitleEn: "Enterprise management",
    tone: "erp",
    icon: "erp" as const,
  },
  {
    id: "mineportal",
    labelFr: "ITaaS MinePortal",
    labelEn: "ITaaS MinePortal",
    subtitleFr: "Portails & Services",
    subtitleEn: "Portals & Services",
    tone: "mineportal",
    icon: "mines" as const,
  },
] as const;

/** Hero KPI cards — illustrative platform signals (ProductStatsBar). */
export const WHY_HERO_KPIS = [
  { value: "8", labelFr: "Modules métiers", labelEn: "Business modules" },
  { value: "100 %", labelFr: "Plateforme intégrée", labelEn: "Integrated platform" },
  { value: "99,9 %", labelFr: "Disponibilité", labelEn: "Availability" },
  { value: "1", labelFr: "Source unique de vérité", labelEn: "Single source of truth" },
] as const;

export const WHY_PROBLEMS = [
  {
    id: "silos",
    titleFr: "Logiciels isolés",
    titleEn: "Isolated software",
    bodyFr: "Chaque métier travaille dans son outil — sans lien naturel entre les équipes.",
    bodyEn: "Each line of business runs in its own tool — with no natural link between teams.",
  },
  {
    id: "reentry",
    titleFr: "Doubles saisies",
    titleEn: "Duplicate data entry",
    bodyFr: "Les mêmes informations sont resaisies d’un système à l’autre, source d’erreurs.",
    bodyEn: "The same information is re-entered from system to system — a source of errors.",
  },
  {
    id: "data",
    titleFr: "Données incohérentes",
    titleEn: "Inconsistent data",
    bodyFr: "Impossible de s’aligner sur une version unique de la vérité opérationnelle.",
    bodyEn: "Teams cannot align on a single version of operational truth.",
  },
  {
    id: "visibility",
    titleFr: "Manque de visibilité",
    titleEn: "Lack of visibility",
    bodyFr: "Le pilotage reste fragmenté : reporting manuel, délais, angles morts.",
    bodyEn: "Steering stays fragmented: manual reporting, delays, and blind spots.",
  },
  {
    id: "cost",
    titleFr: "Coûts qui s’empilent",
    titleEn: "Stacking costs",
    bodyFr: "Licences, intégrations et maintenance se multiplient sans réduire la complexité.",
    bodyEn: "Licenses, integrations and maintenance multiply without reducing complexity.",
  },
  {
    id: "productivity",
    titleFr: "Productivité freinée",
    titleEn: "Constrained productivity",
    bodyFr: "Les équipes passent plus de temps à faire communiquer les outils qu’à exécuter.",
    bodyEn: "Teams spend more time making tools talk than executing the work.",
  },
] as const;

export const WHY_ANSWER_POINTS = [
  {
    id: "connected",
    titleFr: "Modules connectés",
    titleEn: "Connected modules",
    bodyFr: "Les applications métier s’appuient sur une plateforme commune.",
    bodyEn: "Business applications run on a shared platform foundation.",
  },
  {
    id: "dataflow",
    titleFr: "Données qui circulent",
    titleEn: "Data that flows",
    bodyFr: "Les informations utiles traversent les processus sans resaisie systématique.",
    bodyEn: "Useful information moves across processes without systematic re-entry.",
  },
  {
    id: "identity",
    titleFr: "Une seule identité",
    titleEn: "One identity",
    bodyFr: "Accès unifié via ITaaS IAM — un portail, des droits maîtrisés.",
    bodyEn: "Unified access via ITaaS IAM — one portal, controlled permissions.",
  },
  {
    id: "sot",
    titleFr: "Une source de vérité",
    titleEn: "One source of truth",
    bodyFr: "Une plateforme pour aligner équipes, processus et décisions.",
    bodyEn: "One platform to align teams, processes and decisions.",
  },
] as const;

export const WHY_DIFFERENTIATORS = [
  {
    id: "integrated",
    titleFr: "Plateforme intégrée",
    titleEn: "Integrated platform",
    bodyFr: "Les métiers partagent une fondation commune plutôt qu’un patchwork d’outils.",
    bodyEn: "Business domains share a common foundation instead of a patchwork of tools.",
  },
  {
    id: "modular",
    titleFr: "Architecture modulaire",
    titleEn: "Modular architecture",
    bodyFr: "Activez ce dont vous avez besoin, puis composez au rythme de votre organisation.",
    bodyEn: "Activate what you need, then compose at your organization’s pace.",
  },
  {
    id: "cloud",
    titleFr: "Cloud sécurisé",
    titleEn: "Secure cloud",
    bodyFr: "Hébergement et opérations orientés RDC, avec bonnes pratiques de sécurité.",
    bodyEn: "Hosting and operations oriented to the DRC, with security good practices.",
  },
  {
    id: "isolation",
    titleFr: "Isolation par organisation",
    titleEn: "Organization-level isolation",
    bodyFr: "Architecture conçue pour séparer les environnements et protéger les données.",
    bodyEn: "Architecture designed to separate environments and protect data.",
  },
  {
    id: "api",
    titleFr: "API ouvertes",
    titleEn: "Open APIs",
    bodyFr: "Connectez vos systèmes et étendez la Suite selon vos besoins d’intégration.",
    bodyEn: "Connect your systems and extend the Suite according to your integration needs.",
  },
  {
    id: "automation",
    titleFr: "Automatisation",
    titleEn: "Automation",
    bodyFr: "Réduisez les tâches répétitives et fluidifiez les workflows métier.",
    bodyEn: "Reduce repetitive tasks and streamline business workflows.",
  },
  {
    id: "rollout",
    titleFr: "Déploiement progressif",
    titleEn: "Progressive rollout",
    bodyFr: "Démarrez par un périmètre utile, puis étendez sans tout recommencer.",
    bodyEn: "Start with a useful scope, then expand without starting over.",
  },
  {
    id: "local",
    titleFr: "Support local RDC",
    titleEn: "Local DRC support",
    bodyFr: "Éditeur et accompagnement ancrés à Lubumbashi — proximité réelle.",
    bodyEn: "Publisher and support rooted in Lubumbashi — real proximity.",
  },
  {
    id: "guidance",
    titleFr: "Accompagnement",
    titleEn: "Guidance",
    bodyFr: "Paramétrage, formation et suivi pour ancrer l’usage dans vos équipes.",
    bodyEn: "Configuration, training and follow-up to embed usage in your teams.",
  },
] as const;

/** Illustrative orders of magnitude — not contractual KPIs. */
export const WHY_BENEFITS = [
  {
    id: "cost",
    metric: "−",
    labelFr: "Réduction des coûts opérationnels",
    labelEn: "Lower operational costs",
    noteFr: "Moins d’outils redondants à maintenir",
    noteEn: "Fewer redundant tools to maintain",
  },
  {
    id: "time",
    metric: "⏱",
    labelFr: "Gain de temps",
    labelEn: "Time saved",
    noteFr: "Moins de resaisies et de réconciliations manuelles",
    noteEn: "Less re-entry and manual reconciliation",
  },
  {
    id: "automation",
    metric: "⚙",
    labelFr: "Automatisation",
    labelEn: "Automation",
    noteFr: "Workflows plus fluides entre métiers",
    noteEn: "Smoother workflows across domains",
  },
  {
    id: "availability",
    metric: "☁",
    labelFr: "Disponibilité cloud",
    labelEn: "Cloud availability",
    noteFr: "Accès continu aux applications Suite",
    noteEn: "Continuous access to Suite applications",
  },
  {
    id: "visibility",
    metric: "◎",
    labelFr: "Visibilité",
    labelEn: "Visibility",
    noteFr: "Une vue plus claire pour piloter",
    noteEn: "A clearer view for steering",
  },
  {
    id: "decision",
    metric: "▣",
    labelFr: "Décision",
    labelEn: "Decision-making",
    noteFr: "Données plus cohérentes pour décider",
    noteEn: "More coherent data to decide",
  },
] as const;

export const WHY_RDC_POINTS = [
  {
    id: "expertise",
    titleFr: "Expertise locale",
    titleEn: "Local expertise",
    bodyFr: "Une équipe qui connaît le terrain congolais et les contraintes opérationnelles.",
    bodyEn: "A team that knows Congolese ground realities and operational constraints.",
  },
  {
    id: "support",
    titleFr: "Support local",
    titleEn: "Local support",
    bodyFr: "Accompagnement de proximité depuis Lubumbashi, en français.",
    bodyEn: "Nearby support from Lubumbashi, in French.",
  },
  {
    id: "context",
    titleFr: "Contexte africain",
    titleEn: "African context",
    bodyFr: "Produits pensés pour les organisations d’Afrique centrale — pas une adaptation de surface.",
    bodyEn: "Products designed for Central African organizations — not a surface adaptation.",
  },
  {
    id: "compliance",
    titleFr: "Conformité & bonnes pratiques",
    titleEn: "Compliance & good practices",
    bodyFr: "Transparence sur le périmètre publié : disponible, limité ou en développement.",
    bodyEn: "Transparency on published scope: available, limited, or in development.",
  },
  {
    id: "innovation",
    titleFr: "Innovation continue",
    titleEn: "Continuous innovation",
    bodyFr: "Une Suite qui évolue module par module, sans rupture brutale.",
    bodyEn: "A Suite that evolves module by module, without abrupt disruption.",
  },
  {
    id: "partnership",
    titleFr: "Accompagnement durable",
    titleEn: "Lasting partnership",
    bodyFr: "Déploiement, formation et suivi pour ancrer la plateforme dans vos opérations.",
    bodyEn: "Rollout, training and follow-up to embed the platform in your operations.",
  },
] as const;

export function whyCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en ? "Why ITaaS Suite" : "Pourquoi ITaaS Suite",
    seoDescription: en
      ? "Why choose an integrated ITaaS platform instead of disconnected software — modular architecture, unified data, local DRC support."
      : "Pourquoi choisir une plateforme ITaaS intégrée plutôt que des logiciels isolés — architecture modulaire, données unifiées, accompagnement local en RDC.",
    seoKeywords: en
      ? ["why ITaaS", "integrated business platform DRC", "modular suite Congo", "ITaaS RDC"]
      : ["pourquoi ITaaS", "plateforme métier intégrée RDC", "suite modulaire Congo", "ITaaS RDC"],

    heroEyebrow: en ? "Why ITaaS" : "Pourquoi ITaaS",
    heroH1Lead: en ? "One integrated suite. One platform." : "Une suite intégrée. Une plateforme unique.",
    heroH1Emphasis: en ? "Concrete outcomes." : "Des résultats concrets.",
    heroLead: en
      ? "ITaaS Suite unifies your business domains, teams and data in a modular, secure platform. Less complexity. More operational clarity."
      : "ITaaS Suite unifie vos métiers, vos équipes et vos données dans une plateforme modulaire et sécurisée. Moins de complexité. Plus de clarté opérationnelle.",
    heroPillars: en
      ? ([
          "One platform",
          "Unified data",
          "Integrated processes",
          "Consistent experience",
        ] as const)
      : ([
          "Une seule plateforme",
          "Des données unifiées",
          "Des processus intégrés",
          "Une expérience cohérente",
        ] as const),
    heroPrimaryCta: en ? "Discover products" : "Découvrir les produits",
    heroSecondaryCta: en ? "Compose my solution" : "Composer ma solution",
    heroPrimaryHref: "/products",
    heroSecondaryHref: "/configure",
    platformCenter: "ITaaS",
    platformNodes: en ? WHY_PLATFORM_NODES_EN : WHY_PLATFORM_NODES_FR,
    platformCaption: en
      ? "Conceptual view of the ITaaS Suite — connected business modules on one platform. Not a product screenshot."
      : "Vue conceptuelle de l’écosystème ITaaS Suite — modules métier connectés sur une plateforme. Ce n’est pas une capture produit.",

    problemsEyebrow: en ? "The cost of fragmentation" : "Le coût de la fragmentation",
    problemsH2: en
      ? "Why organizations lose time today"
      : "Pourquoi les entreprises perdent du temps aujourd’hui",
    problemsLead: en
      ? "Disconnected tools create friction that compounds every day — across teams, data and decisions."
      : "Des outils déconnectés créent des frictions qui s’accumulent chaque jour — entre équipes, données et décisions.",

    answerEyebrow: en ? "The ITaaS response" : "La réponse ITaaS",
    answerH2: en ? "One platform. Connected modules." : "Une plateforme. Des modules connectés.",
    answerLead: en
      ? "Instead of stacking independent software, ITaaS Suite connects business applications on a shared foundation."
      : "Au lieu d’empiler des logiciels indépendants, ITaaS Suite connecte des applications métier sur une fondation commune.",

    differentEyebrow: en ? "Differentiation" : "Différenciation",
    differentH2: en ? "Why ITaaS is different" : "Pourquoi ITaaS est différent",
    differentLead: en
      ? "A conviction platform: facts over slogans — what changes when you choose an integrated suite."
      : "Une page de conviction : des faits plutôt que des slogans — ce qui change quand vous choisissez une suite intégrée.",

    benefitsEyebrow: en ? "Outcomes" : "Bénéfices",
    benefitsH2: en ? "Concrete benefits" : "Des bénéfices concrets",
    benefitsLead: en
      ? "Illustrative themes — not contractual KPIs. The value depends on your scope and adoption."
      : "Thèmes illustratifs — non contractuels. La valeur dépend de votre périmètre et de votre adoption.",
    benefitsDisclaimer: en
      ? "Illustrative only — not a performance guarantee."
      : "Illustratif uniquement — pas une garantie de performance.",

    sectorsEyebrow: en ? "Sectors" : "Secteurs",
    sectorsH2: en ? "A platform for every sector" : "Une plateforme pour tous les secteurs",
    sectorsLead: en
      ? "Start from your industry reality, then compose the ITaaS products that fit."
      : "Partez de la réalité de votre secteur, puis composez les produits ITaaS adaptés.",
    sectorsCta: en ? "Explore all sectors" : "Explorer tous les secteurs",
    sectorsHref: "/industries",

    rdcEyebrow: en ? "ITaaS RDC" : "ITaaS RDC",
    rdcH2: en ? "Why ITaaS in the DRC?" : "Pourquoi ITaaS RDC ?",
    rdcLead: en
      ? "A Congolese software publisher — designed, supported and accompanied from Lubumbashi."
      : "Un éditeur logiciel congolais — conçu, supporté et accompagné depuis Lubumbashi.",
    rdcAboutCta: en ? "About ITaaS RDC" : "À propos d’ITaaS RDC",
    rdcAboutHref: "/about",

    finalH2: en
      ? "Ready to choose an integrated platform?"
      : "Prêt à choisir une plateforme intégrée ?",
    finalLead: en
      ? "Discover the products, compose a first scope, or talk with the ITaaS RDC team."
      : "Découvrez les produits, composez un premier périmètre, ou échangez avec l’équipe ITaaS RDC.",
    finalPrimaryCta: en ? "Request a demonstration" : "Demander une démonstration",
    finalPrimaryHref: "/contact?type=demo",
    finalSecondaryCta: en ? "Contact us" : "Nous contacter",
    finalSecondaryHref: "/contact",
  };
}
